import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ImpactPanel from "../components/ImpactPanel";
import { CAMPAIGNS, co2PerTreeToDate } from "../impact";

const fmtDate = (iso) =>
  iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }) : "";

// Greenness over time, with the planting date marked.
function GreennessChart({ points, planted }) {
  if (!points.length) return <p className="text-sm text-gray-500">No clear satellite passes yet.</p>;
  const W = 720, H = 220, L = 40, B = 28;
  const t = (d) => new Date(d).getTime();
  const t0 = t(points[0].date), t1 = t(points[points.length - 1].date);
  const x = (d) => L + ((t(d) - t0) / (t1 - t0 || 1)) * (W - L - 10);
  const y = (v) => 10 + (1 - Math.min(Math.max(v, 0), 0.8) / 0.8) * (H - B - 10);
  // 45-day rolling median for the line
  const line = points.map((p) => {
    const w = points.filter((q) => Math.abs(t(q.date) - t(p.date)) <= 22 * 864e5).map((q) => q.ndvi).sort((a, b) => a - b);
    return `${x(p.date).toFixed(1)},${y(w[Math.floor(w.length / 2)]).toFixed(1)}`;
  });
  const years = [];
  for (let yr = new Date(t0).getFullYear() + 1; yr <= new Date(t1).getFullYear(); yr++) years.push(yr);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Greenness of the farm over time">
      {[0, 0.2, 0.4, 0.6, 0.8].map((v) => (
        <g key={v}>
          <line x1={L} x2={W - 10} y1={y(v)} y2={y(v)} stroke="#e5e7eb" />
          <text x={L - 6} y={y(v) + 4} fontSize="10" textAnchor="end" fill="#6b7280">{v.toFixed(1)}</text>
        </g>
      ))}
      {years.map((yr) => (
        <text key={yr} x={x(`${yr}-01-01`)} y={H - 8} fontSize="10" textAnchor="middle" fill="#6b7280">{yr}</text>
      ))}
      {planted && t(planted) > t0 && (
        <g>
          <line x1={x(planted)} x2={x(planted)} y1={8} y2={H - B} stroke="#c0392b" strokeDasharray="4 3" />
          <text x={x(planted) + 4} y={20} fontSize="10" fill="#c0392b">planted {fmtDate(planted)}</text>
        </g>
      )}
      {points.map((p) => (
        <circle key={p.date} cx={x(p.date)} cy={y(p.ndvi)} r="2.2" fill="#3f8f4f" opacity="0.6" />
      ))}
      <polyline points={line.join(" ")} fill="none" stroke="#1f5f2f" strokeWidth="1.8" />
    </svg>
  );
}

function FarmFromSpace() {
  const { id } = useParams();
  const [farm, setFarm] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`/space/${id}.json`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("not found"))))
      .then(setFarm)
      .catch(() => setError("We couldn't load this farm right now."));
  }, [id]);

  if (error) return <div className="max-w-3xl mx-auto py-16 px-4 text-center text-gray-600">{error}</div>;
  if (!farm) return <div className="max-w-3xl mx-auto py-16 px-4 text-center text-gray-500">Loading the view from space...</div>;

  const age = (Date.now() - new Date(farm.planted)) / (365.25 * 864e5);
  const before = farm.images.filter((im) => im.date < farm.planted).slice(-2);
  const after = farm.images.filter((im) => im.date >= farm.planted);
  const strip = [...before, ...after.filter((_, i) => i % 2 === 1 || i === after.length - 1)].slice(-8);
  const preHighres = farm.highres.filter((h) => h.captured < farm.planted).slice(-1)[0];
  const postHighres = farm.highres.filter((h) => h.captured >= farm.planted);

  return (
    <div className="py-8 px-4">
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <Link to="/farms" className="text-sm text-sritaru-700 underline">← All farms from space</Link>
          <h1 className="text-3xl font-bold text-sritaru-800 mt-2">🛰️ Your trees from space</h1>
          <p className="text-gray-700 mt-1">
            {farm.farmer}'s farm · {farm.village}, {farm.district} ({farm.state}) · {farm.trees_on_farm.toLocaleString()}{" "}
            {farm.species} trees planted {fmtDate(farm.planted)}
          </p>
          <p className="text-sm text-gray-500">
            {age.toFixed(1)} years old · about {Math.round(co2PerTreeToDate(age))} kg of CO₂ absorbed per tree so far (estimate)
          </p>
        </div>

        <section className="bg-white rounded-xl shadow-md p-5">
          <h2 className="text-xl font-bold text-sritaru-700 mb-1">Close-up: the trees themselves</h2>
          <p className="text-sm text-gray-600 mb-3">
            Very sharp satellite photos (about 30–50 cm per pixel) are taken over this farm every year or two.
            In the newest ones you can see the rows of young trees. They open in Esri's World Imagery Wayback viewer.
          </p>
          <div className="flex flex-wrap gap-2">
            {preHighres && (
              <a href={preHighres.viewer} target="_blank" rel="noopener noreferrer"
                 className="border border-gray-300 rounded-lg px-3 py-2 text-sm hover:bg-gray-50">
                Before planting · {fmtDate(preHighres.captured)}
              </a>
            )}
            {postHighres.map((h) => (
              <a key={h.release} href={h.viewer} target="_blank" rel="noopener noreferrer"
                 className="bg-sritaru-600 text-white rounded-lg px-3 py-2 text-sm hover:bg-sritaru-700">
                After planting · {fmtDate(h.captured)}
              </a>
            ))}
            {!postHighres.length && <span className="text-sm text-gray-500">No sharp photo since planting yet; we'll add it when one is taken.</span>}
          </div>
        </section>

        <section className="bg-white rounded-xl shadow-md p-5">
          <h2 className="text-xl font-bold text-sritaru-700 mb-1">How green is the farm?</h2>
          <p className="text-sm text-gray-600 mb-3">
            Every few days the Sentinel-2 satellite passes over and measures how green a 40 m × 40 m patch of the
            planting is. The dots are clear-sky passes; the line is the trend. Green rises every monsoon, so compare
            the same season year to year. Each pixel is 10 m across, so young trees are too small to see on their
            own; the reading also includes grass and crops between the rows. The sharp photos above show the trees.
          </p>
          <GreennessChart points={farm.ndvi} planted={farm.planted} />
          {strip.length > 0 && (
            <>
              <p className="text-sm text-gray-600 mt-4 mb-2">The farm and its surroundings (600 m across, 10 m per pixel), one view per season. These show the land, not single trees:</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {strip.map((im) => (
                  <figure key={im.file} className="text-center">
                    <img src={`/space/${im.file}`} alt={`Satellite view on ${fmtDate(im.date)}`} className="w-full rounded" loading="lazy" />
                    <figcaption className="text-[11px] text-gray-500 mt-1">
                      {fmtDate(im.date)}{im.date < farm.planted ? " · before" : ""}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </>
          )}
          <p className="text-[10px] text-gray-400 mt-3">
            Contains modified Copernicus Sentinel data (ESA). Updated {fmtDate(farm.updated)}.
          </p>
        </section>

        <ImpactPanel treesByYear={{ [farm.campaign]: farm.trees_on_farm }} speciesMix={{ [farm.species]: 1 }} title="This farm's impact" />
        {!CAMPAIGNS[farm.campaign]?.confirmed && (
          <p className="text-xs text-gray-400 text-center">Species and planting date for this campaign are estimates.</p>
        )}
      </div>
    </div>
  );
}

export default FarmFromSpace;
